<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NInput, NPopconfirm, NTag, useMessage } from 'naive-ui'
import { RouterLink } from 'vue-router'
import type { SynthesisSuggestion } from '../stores/synthesis_suggestions'
import { useSynthesisSuggestionsStore } from '../stores/synthesis_suggestions'
import { formatAbsoluteDate, formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'

// One reviewable row in /synthesis. Header (kind tag + proposed_path
// link + title + age), justification block, source-pages list,
// Preview→Edit→Accept flow on the drafted body.
//
// Unlike LintNoteCard (which renders a structured diff and offers
// Accept/Edit/Dismiss with the edit going through a regenerate modal),
// SynthesisSuggestionCard owns its editor inline — proposed_body is
// freeform markdown, the admin tweaks it in a textarea, clicks Save
// (PATCH), then Accept (POST). This matches the UX decision of
// Preview→Edit→Accept living on the same surface.

const props = defineProps<{
  suggestion: SynthesisSuggestion
}>()

// Right-aligning the popconfirm and capping it at this width (well under
// viewport - margins) mirrors the lint card so it fits inside a narrow
// mobile viewport without horizontal overflow.
const POPCONFIRM_WIDTH = 260

const store = useSynthesisSuggestionsStore()
const message = useMessage()
const now = useNow()

const acting = ref(false)
const editing = ref(false)
const draftBody = ref(props.suggestion.proposed_body)

// Render the drafted body as markdown so the admin evaluates the
// formatted draft, not raw syntax. `rawPreview` flips back to the
// plain source for copy/diff.
const wikiMarkdown = useWikiMarkdown()
const rawPreview = ref(false)
const renderedBody = computed(() => wikiMarkdown.render(props.suggestion.proposed_body, props.suggestion.proposed_path))

// A supersede refreshes an existing synthesis page in place. The
// card shows the current page body beside the drafted replacement so the
// admin can see what changes before accepting.
const isSupersede = computed(() => props.suggestion.supersedes_path != null)
const renderedExisting = computed(() =>
  wikiMarkdown.render(props.suggestion.existing_target_content, props.suggestion.proposed_path),
)

const KIND_TAG_TYPES: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  contradiction_reconciliation: 'error',
  emerging_theme: 'success',
  concept_gap: 'info',
  cross_page_pattern: 'warning',
}
const kindTagType = computed(() => KIND_TAG_TYPES[props.suggestion.kind] ?? 'info')

const formattedKind = computed(() => props.suggestion.kind.replaceAll('_', ' '))

const targetLabel = computed(() => {
  if (props.suggestion.supersedes_path != null) return `Refresh ${props.suggestion.proposed_path}`
  if (props.suggestion.append_to_existing) return `Append to ${props.suggestion.proposed_path}`
  return `Create ${props.suggestion.proposed_path}`
})

function startEdit() {
  draftBody.value = props.suggestion.proposed_body
  editing.value = true
}

function cancelEdit() {
  editing.value = false
  draftBody.value = props.suggestion.proposed_body
}

async function saveEdit() {
  acting.value = true
  try {
    await store.update(props.suggestion.id, { proposed_body: draftBody.value })
    message.success('Draft saved.')
    editing.value = false
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save')
  } finally {
    acting.value = false
  }
}

async function onAccept() {
  acting.value = true
  try {
    await store.accept(props.suggestion.id)
    message.success('Accept queued.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to accept')
  } finally {
    acting.value = false
  }
}

async function onDismiss() {
  acting.value = true
  try {
    await store.dismiss(props.suggestion.id)
    message.success('Dismiss queued.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to dismiss')
  } finally {
    acting.value = false
  }
}
</script>

<template>
  <article
    class="border border-white/10 rounded p-4 flex flex-col gap-3"
    data-testid="synthesis-suggestion-card"
    :data-suggestion-id="suggestion.id"
  >
    <header class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1 min-w-0">
        <div class="flex items-center gap-2">
          <NTag size="small" :type="kindTagType">{{ formattedKind }}</NTag>
          <NTag v-if="isSupersede" size="small" type="warning" :bordered="false" data-testid="synthesis-supersede-tag"
            >refresh</NTag
          >
          <span class="text-sm font-mono text-white/80 truncate">{{ targetLabel }}</span>
        </div>
        <p class="text-base font-semibold text-white m-0">{{ suggestion.title }}</p>
        <p class="text-sm text-white/70 m-0">{{ suggestion.justification }}</p>
      </div>
      <time
        :datetime="suggestion.created_at"
        :title="formatAbsoluteDate(suggestion.created_at)"
        class="text-xs text-white/40 shrink-0"
        >{{ formatDate(suggestion.created_at, now) }}</time
      >
    </header>

    <section class="flex flex-col gap-1" data-testid="synthesis-sources">
      <h4 class="text-xs uppercase tracking-wide text-white/40 m-0">Drawn from</h4>
      <ul class="list-none p-0 m-0 flex flex-col gap-0.5">
        <li v-for="path in suggestion.source_paths" :key="path">
          <RouterLink :to="`/p/${path}`" class="text-sm font-mono text-white/70 hover:text-white no-underline">{{
            path
          }}</RouterLink>
        </li>
      </ul>
    </section>

    <section v-if="isSupersede && !editing" data-testid="synthesis-supersede-current" class="flex flex-col gap-1">
      <h4 class="text-xs uppercase tracking-wide text-white/40 m-0">Current page (will be overwritten)</h4>
      <article
        class="wiki-markdown text-sm bg-amber-500/[0.04] border border-amber-500/20 rounded p-3 opacity-80"
        v-html="renderedExisting"
      />
    </section>

    <section v-if="!editing" data-testid="synthesis-preview">
      <div class="flex items-baseline justify-between gap-2 mb-1">
        <h4 class="text-xs uppercase tracking-wide text-white/40 m-0">
          {{ isSupersede ? 'Proposed replacement' : 'Drafted body' }}
        </h4>
        <button
          type="button"
          class="text-xs text-white/40 hover:text-white"
          data-testid="synthesis-preview-toggle"
          @click="rawPreview = !rawPreview"
        >
          {{ rawPreview ? 'Preview' : 'Raw' }}
        </button>
      </div>
      <pre
        v-if="rawPreview"
        class="whitespace-pre-wrap text-sm text-white/90 bg-white/[0.03] border border-white/10 rounded p-3 m-0 overflow-x-auto"
        >{{ suggestion.proposed_body }}</pre>
      <article
        v-else
        class="wiki-markdown text-sm bg-white/[0.03] border border-white/10 rounded p-3"
        data-testid="synthesis-preview-rendered"
        v-html="renderedBody"
      />
    </section>

    <section v-else data-testid="synthesis-editor" class="flex flex-col gap-2">
      <h4 class="text-xs uppercase tracking-wide text-white/40 m-0">Editing drafted body</h4>
      <NInput
        v-model:value="draftBody"
        type="textarea"
        :autosize="{ minRows: 10, maxRows: 30 }"
        :disabled="acting"
        data-testid="synthesis-editor-input"
      />
    </section>

    <footer class="flex justify-end gap-2">
      <template v-if="editing">
        <NButton :disabled="acting" data-testid="synthesis-edit-cancel" @click="cancelEdit">Cancel</NButton>
        <NButton type="primary" :disabled="acting" data-testid="synthesis-edit-save" @click="saveEdit"
          >Save draft</NButton
        >
      </template>
      <template v-else>
        <!-- `placement="top-end"` + capped `width` mirror the lint
             card so the popconfirm fits inside a narrow mobile viewport
             instead of overflowing the right edge. -->
        <NPopconfirm
          placement="top-end"
          :width="POPCONFIRM_WIDTH"
          positive-text="Dismiss"
          negative-text="Cancel"
          @positive-click="onDismiss"
        >
          <template #trigger>
            <NButton :disabled="acting" data-testid="synthesis-dismiss">Dismiss</NButton>
          </template>
          Dismiss this suggestion? The next monthly Synthesis pass will see the dismissal and won't re-suggest the same
          cluster.
        </NPopconfirm>
        <NButton :disabled="acting" data-testid="synthesis-edit" @click="startEdit">Edit</NButton>
        <NPopconfirm
          placement="top-end"
          :width="POPCONFIRM_WIDTH"
          positive-text="Accept draft"
          negative-text="Cancel"
          @positive-click="onAccept"
        >
          <template #trigger>
            <NButton type="primary" :disabled="acting" data-testid="synthesis-accept">Accept</NButton>
          </template>
          Apply this draft? It will
          {{ isSupersede ? 'overwrite' : suggestion.append_to_existing ? 'be appended to' : 'be written to' }}
          <code>{{ suggestion.proposed_path }}</code> as one atomic commit.
        </NPopconfirm>
      </template>
    </footer>
  </article>
</template>
